import clsx from 'clsx'
import { KTIcon, toAbsoluteUrl, useDebounceOptionType } from '../../../helpers'
import { HeaderUserMenu } from '../../../partials'
import { OptionType, useLayout } from '../../core'

import { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import Select from 'react-select'
import { CompanyListTransformedResp, getCompaniesList } from './api'

const itemClass = 'ms-1 ms-lg-3'
const userAvatarClass = 'symbol-35px symbol-md-40px'

const Navbar = () => {
  const {
    company,
    setCompany,
    financialYear,
    setFinancialYear,
    trigger,
    companyOptions,
    setCompanyOptions,
    setFyStartDate,
    setFyEndDate,
  } = useLayout()
  const [companyYearObj, setCompanyYearObj] = useState<CompanyListTransformedResp>()
  const debouncedCompany = useDebounceOptionType(company, 200)
  const [yearOptions, setYearOptions] = useState<OptionType[]>([])
  const companySelectStyles = useMemo(
    () => ({
      container: (baseStyles: any) => ({
        ...baseStyles,
        width: '100%',
        minWidth: 0,
      }),
      valueContainer: (baseStyles: any) => ({
        ...baseStyles,
        minWidth: 0,
        overflow: 'hidden',
        flexWrap: 'nowrap',
      }),
      singleValue: (baseStyles: any) => ({
        ...baseStyles,
        maxWidth: '100%',
        margin: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        color: 'var(--bs-gray-800)',
      }),
      placeholder: (baseStyles: any) => ({
        ...baseStyles,
        maxWidth: '100%',
        margin: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }),
      input: (baseStyles: any) => ({
        ...baseStyles,
        margin: 0,
        padding: 0,
      }),
      menu: (baseStyles: any) => ({
        ...baseStyles,
        width: '100%',
        minWidth: '100%',
      }),
      option: (baseStyles: any) => ({
        ...baseStyles,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }),
    }),
    []
  )

  const getCompanyList = useCallback(async () => {
    try {
      const res: any = await getCompaniesList()
      if (!res) {
        throw new Error('Something Went wrong, Please Try Again')
      }
      if ('status' in res) {
        throw new Error(res.message)
      }

      setCompanyYearObj(res)

      const companies = Object.keys(res).map((cin) => ({
        label: res[cin].companyName,
        value: cin,
      }))
      if (!companyOptions.length && companies.length > 0) {
        setCompanyOptions(companies)

        if (!company.value) {
          setCompany(companies[companies.length - 1])
        }
      } else if (companyOptions.length > 0 && companies.length > companyOptions.length) {
        setCompany(companies[companyOptions.length - 1])
      } else {
        setCompany({
          label: '',
          value: '',
        })
      }
    } catch (error: any) {
      setCompany({
        label: '',
        value: '',
      })
      setFinancialYear({label: '', value: ''})
      setFyStartDate('')
      setFyEndDate('')
      toast.error(error.message || 'An unexpected error occurred', {})
    }
  }, [])

  useEffect(() => {
    if (trigger) getCompanyList()
  }, [trigger])

  useEffect(() => {
    if (debouncedCompany && debouncedCompany?.value) {
      const years = companyYearObj?.[debouncedCompany.value]?.fyList.map((yr) => ({
        label: yr,
        value: yr,
      }))
      if (years?.length) {
        setYearOptions(years)
        setFinancialYear({
          value: '',
          label: '',
        })
        setFinancialYear(years[0])
      }
    } else {
      setFinancialYear({
        value: '',
        label: '',
      })
    }
  }, [debouncedCompany, companyYearObj, setFinancialYear, trigger])

  const debouncedFinancialYear = useDebounceOptionType(financialYear, 150)

  useEffect(() => {
    if (
      companyYearObj &&
      debouncedCompany?.value &&
      companyYearObj[debouncedCompany.value]?.fyDetails &&
      debouncedFinancialYear?.value
    ) {
      try {
        const fyDetails =
          companyYearObj[debouncedCompany.value].fyDetails[debouncedFinancialYear.value]
        if (fyDetails) {
          setFyStartDate(fyDetails.startDate)
          setFyEndDate(fyDetails.endDate)
        }
      } catch (error) {
        console.error('Error setting fiscal year dates:', error)
      }
    }
  }, [companyYearObj, debouncedFinancialYear, debouncedCompany, trigger])

  const handleCompanyChange = (option: OptionType | null) => {
    if (company.value === option?.value) return
    setFinancialYear({
      value: '',
      label: '',
    })

    setCompany(() => option as OptionType)
  }

  return (
    <div className='app-navbar'>
      <div className='app-navbar-item navbar-select-wrapper company-select-wrapper'>
        <div className='navbar-icon-prefix'>
          <KTIcon iconName='briefcase' className='fs-2' />
        </div>
        <Select
          value={company}
          isDisabled={!company?.value}
          onChange={handleCompanyChange}
          options={companyOptions}
          isSearchable={true}
          classNamePrefix='react-select'
          styles={companySelectStyles}
          components={{IndicatorSeparator: () => null}}
        />
      </div>

      <div className='app-navbar-item navbar-select-wrapper fy-select-wrapper'>
        <div className='navbar-icon-prefix'>
          <i className='fa-solid fa-calendar-days' />
        </div>
        <Select
          value={financialYear}
          onChange={(option: any) => setFinancialYear(option as OptionType)}
          isDisabled={!company?.value}
          options={yearOptions}
          isSearchable={false}
          classNamePrefix='react-select'
          components={{IndicatorSeparator: () => null}}
        />
      </div>

      <div className={clsx('app-navbar-item', itemClass)}>
        <div
          className={clsx('cursor-pointer symbol', userAvatarClass)}
          data-kt-menu-trigger="{default: 'click'}"
          data-kt-menu-attach='parent'
          data-kt-menu-placement='bottom-end'
        >
          <img src={toAbsoluteUrl('/media/avatars/Default.jpg')} alt='' />
        </div>
        <HeaderUserMenu />
      </div>
    </div>
  )
}

export { Navbar }
